
import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Avatar,
  Divider,
  CircularProgress,
  Alert,
  Stack,
  Button,
} from '@mui/material';
import {
  AccessTime as TimeIcon,
  CalendarToday as CalendarIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import appointmentService from '../services/appointmentService';
import { useTheme } from '../contexts/ThemeContext';
import { createCommonStyles } from '../theme/styles';
import { useNavigate } from "react-router-dom";

const LastAndNextAppointment = () => {
  const { user } = useSelector((state) => state.auth);
  const { isDarkMode } = useTheme();
  const commonStyles = createCommonStyles(isDarkMode);
  const [lastAppointment, setLastAppointment] = useState(null);
  const [nextAppointment, setNextAppointment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const navigate = useNavigate();

  useEffect(() => {
    const fetchAppointments = async () => {
      try {
        setLoading(true);
        setError(null);

        // Za demo mode, koristi demo-tenant-1
        const tenantId = user?.tenantId?._id || user?.tenantId || 'demo-tenant-1';
        const userId = user?._id || 'demo-user-1';

        const response = await appointmentService.getLastAndNextAppointment(
          userId,
          tenantId,
        );

        if (response) {
          // Formatiraj podatke za prikaz
          const formatAppointment = (apt) => {
            if (!apt) return null;
            
            const appointmentDate = new Date(apt.appointmentDate);
            const dateString = appointmentDate.toLocaleDateString('sr-RS', {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            });
            
            const timeString = apt.selectedTime || '';
            
            return {
              ...apt,
              dateString,
              timeString
            };
          };
          
          setLastAppointment(formatAppointment(response.lastAppointment));
          setNextAppointment(formatAppointment(response.nextAppointment));
        }

      } catch (err) {
        console.error('Error fetching appointments:', err);
        setError('Greška pri učitavanju termina');
      } finally {
        setLoading(false);
      }
    };

    if(user){
      fetchAppointments();
    }
  }, [user]);

  const handleReserveClick = () => {
   navigate("/appointment-booking/new");
  };

  const AppointmentCard = ({ appointment, title, icon, emptyMessage, showReserveButton = false }) => (
    <Card 
      elevation={3}
      sx={{ 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
      }}
    >
      <CardContent sx={{ flexGrow: 1, p: 3 }}>
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
          <Avatar sx={{ bgcolor: 'primary.main', width: 48, height: 48 }}>
            {icon}
          </Avatar>
          <Typography variant="h6" fontWeight="bold" color="primary">
            {title}
          </Typography>
        </Stack>

        {appointment ? (
          <>
            {/* Datum i vreme */}
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <CalendarIcon color="action" fontSize="small" />
                <Typography variant="body1" fontWeight="medium">
                  {appointment.dateString}
                </Typography>
              </Stack>
              <Stack direction="row" alignItems="center" spacing={1}>
                <TimeIcon color="primary" fontSize="small" />
                <Typography variant="body1" fontWeight="medium" color="primary">
                  {appointment.timeString}
                </Typography>
              </Stack>
            </Stack>
            <Divider sx={{ my: 2 }} />
            {/* Usluge */}
            {appointment.serviceIds && appointment.serviceIds.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1, color: 'text.secondary' }}>
                  Usluge:
                </Typography>
                {appointment.serviceIds.map((service, index) => (
                  <Stack key={index} direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <TimeIcon color="action" fontSize="small" />
                      <Typography variant="body2" fontWeight="medium">
                        {service.name}
                      </Typography>
                      {service.duration && (
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          ({service.duration} min)
                        </Typography>
                      )}
                    </Stack>
                    {service.price && (
                      <Typography variant="body2" fontWeight="bold" color="primary">
                        {service.price.amount} RSD
                      </Typography>
                    )}
                  </Stack>
                ))}
              </Box>
            )}
          </>
        ) : (
          <Box 
            sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center',
              minHeight: 200,
              color: 'text.secondary'
            }}
          >
            <Typography variant="body1" textAlign="center" sx={{ mb: showReserveButton ? 3 : 0 }}>
              {emptyMessage}
            </Typography>
            {showReserveButton && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleReserveClick}
                sx={commonStyles.gradientButton}
              >
                Rezervišite termin
              </Button>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" fontWeight="bold" sx={{ mb: 3 }}>
        Poslednji i naredni termin
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <AppointmentCard
            appointment={lastAppointment}
            title="Poslednji termin"
            icon={<CalendarIcon />}
            emptyMessage="Nemate završene termine"
          />
        </Grid>
        
        <Grid item xs={12} md={6}>
          <AppointmentCard
            appointment={nextAppointment}
            title="Naredni termin"
            icon={<TimeIcon />}
            emptyMessage="Nemate zakazane buduće termine"
            showReserveButton={true}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default LastAndNextAppointment;